/**
 * Composable for video playlist navigation with loop support
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import type { VideoAssetListItem } from '../../types/asset'

export interface UseVideoNavigationOptions {
  items: Ref<VideoAssetListItem[]>
  initialIndex?: number
  loop?: boolean
  autoAdvance?: boolean
  onIndexChange?: (index: number) => void
  onTrackEnd?: (index: number) => void
  onCollectionEnd?: () => void
  /**
   * Asked when an entry ends and another follows: `true` holds the playlist on the entry that
   * ended — its end-of-video cards are up — until `resumeAdvance` is called or the viewer moves.
   */
  holdAtEnd?: () => boolean
  /** Called when the playlist holds, so the player can say the entry has ended. */
  onHold?: () => void
}

export interface UseVideoNavigationReturn {
  currentIndex: Ref<number>
  currentItem: ComputedRef<VideoAssetListItem | undefined>
  totalItems: ComputedRef<number>

  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isFirstItem: ComputedRef<boolean>
  isLastItem: ComputedRef<boolean>

  isLoop: Ref<boolean>
  /** Whether the playlist is held on an entry that ended, waiting for its end cards. */
  isHolding: Ref<boolean>

  goToIndex: (index: number) => void
  next: () => boolean
  prev: () => void
  first: () => void
  last: () => void

  toggleLoop: () => void
  setLoop: (enabled: boolean) => void

  handleTrackEnd: () => void
  /** Move on from a hold: to the next entry, as the end of the entry would have. */
  resumeAdvance: () => void
}

export function useVideoNavigation(options: UseVideoNavigationOptions): UseVideoNavigationReturn {
  const {
    items,
    initialIndex = 0,
    loop = false,
    autoAdvance = true,
    onIndexChange,
    onTrackEnd,
    onCollectionEnd,
    holdAtEnd,
    onHold,
  } = options

  const currentIndex = ref(initialIndex)
  const isLoop = ref(loop)
  const isHolding = ref(false)

  const totalItems = computed(() => items.value.length)
  const currentItem = computed(() => items.value[currentIndex.value])

  const canGoNext = computed(() => {
    if (totalItems.value === 0) return false
    return isLoop.value || currentIndex.value < totalItems.value - 1
  })

  const canGoPrev = computed(() => {
    if (totalItems.value === 0) return false
    return isLoop.value || currentIndex.value > 0
  })

  const isFirstItem = computed(() => currentIndex.value === 0)
  const isLastItem = computed(() => currentIndex.value === totalItems.value - 1)

  function goToIndex(index: number): void {
    if (totalItems.value === 0) return

    let validIndex = index
    if (isLoop.value) {
      if (index < 0) validIndex = totalItems.value - 1
      else if (index >= totalItems.value) validIndex = 0
    } else {
      validIndex = Math.max(0, Math.min(index, totalItems.value - 1))
    }

    if (validIndex !== currentIndex.value) {
      isHolding.value = false
      currentIndex.value = validIndex
      onIndexChange?.(validIndex)
    }
  }

  function next(): boolean {
    if (!canGoNext.value && !isLoop.value) return false
    goToIndex(currentIndex.value + 1)
    return true
  }

  function prev(): void {
    if (!canGoPrev.value) return
    goToIndex(currentIndex.value - 1)
  }

  function first(): void {
    goToIndex(0)
  }

  function last(): void {
    goToIndex(totalItems.value - 1)
  }

  function toggleLoop(): void {
    isLoop.value = !isLoop.value
  }

  function setLoop(enabled: boolean): void {
    isLoop.value = enabled
  }

  function handleTrackEnd(): void {
    onTrackEnd?.(currentIndex.value)

    if (!autoAdvance) return

    if (canGoNext.value) {
      if (holdAtEnd?.()) {
        isHolding.value = true
        onHold?.()
        return
      }
      next()
    } else if (!isLoop.value) {
      onCollectionEnd?.()
    }
  }

  function resumeAdvance(): void {
    if (!isHolding.value) return
    isHolding.value = false
    next()
  }

  watch(
    () => items.value.length,
    (newLength) => {
      if (currentIndex.value >= newLength) {
        currentIndex.value = Math.max(0, newLength - 1)
      }
    },
  )

  return {
    currentIndex,
    currentItem,
    totalItems,
    canGoNext,
    canGoPrev,
    isFirstItem,
    isLastItem,
    isLoop,
    isHolding,
    goToIndex,
    next,
    prev,
    first,
    last,
    toggleLoop,
    setLoop,
    handleTrackEnd,
    resumeAdvance,
  }
}
